import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Search, CheckCircle2, RotateCcw, MessageCirclePlus } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { cn } from '../../lib/utils';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { useDiagramComments } from '../../hooks/comments/useDiagramComments';
import { useDiagramCommentsQuery } from '../../hooks/comments/useCommentsQueries';
import { useDiagramMentionables } from '../../hooks/comments';
import { useCommentOverlayStore } from '../../stores/commentOverlayStore';
import { useDiagramStore } from '../../stores/diagramStore';
import { useOrgChartViewStore } from '../../stores/orgchartViewStore';
import type { CommentNode } from '../../domains/comments/models/Comment';
import { OrgChartPerspectiveProjection } from '../../domains/orgchart/services/OrgChartPerspectiveProjection';
import { applyOrgChartCollapse } from '../../domains/orgchart/services/orgChartCollapseProjection';
import { computeOrgReportingTargets, isMatrixEdge, isReportsToEdge } from '../../domains/orgchart/services/orgChartReportCounts';
import {
  resolveCommentAuthorVisual,
  buildDisplayName,
} from '../../lib/comments/commentAuthor';
import { LinearProgress } from '../ui/linear-progress';
import { formatRelativeTime } from '../../lib/datetime/relativeTime';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Logger } from '../../utils/logger';
import { useAuthStore } from '../../stores/authStore';

const logger = Logger.getLoggerWithPrefix('CommentsSidePanel');

interface CommentsSidePanelProps {
  diagramId: string | undefined;
  currentUserId?: string;
}

// Normalise search input for case-insensitive matching.
function normaliseSearchTerm(term: string): string {
  return term.trim().toLowerCase();
}

// Count replies across the full nested thread.
function countReplies(nodes: CommentNode[] | undefined): number {
  if (!nodes || nodes.length === 0) {
    return 0;
  }
  return nodes.reduce((total, node) => total + 1 + countReplies(node.replies), 0);
}

export const CommentsSidePanel: React.FC<CommentsSidePanelProps> = ({ diagramId, currentUserId }) => {
  const { t, i18n } = useTranslation('common');
  const timeLocale = i18n.resolvedLanguage ?? i18n.language;
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [searchTerm, setSearchTerm] = useState('');
  const { diagramType } = useDiagramDocument();
  const nodes = useDiagramStore((state) => state.nodes);
  const edges = useDiagramStore((state) => state.edges);
  const asOfDate = useOrgChartViewStore((state) => state.asOfDate);
  const activeViewpoint = useOrgChartViewStore((state) => state.activeViewpoint);
  const collapsedNodeIds = useOrgChartViewStore((state) => state.collapsedNodeIds);
  const toggleCollapsed = useOrgChartViewStore((state) => state.toggleCollapsed);
  const { data: mentionables = [] } = useDiagramMentionables(diagramId);
  const mentionableMap = useMemo(() => new Map(mentionables.map((m) => [m.id, m])), [mentionables]);
  const handleBeginCommentPlacement = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('canvas-action', {
        detail: { action: 'begin-comment-placement' },
      }),
    );
  }, []);

  // Prefer root body text, but fall back to the first reply snippet.
  const buildThreadPreview = (comment: CommentNode): string => {
    const body = comment.body?.trim();
    if (body && body.length > 0) {
      return body;
    }
    const firstReply = comment.replies?.[0]?.body?.trim();
    if (firstReply && firstReply.length > 0) {
      return firstReply;
    }
    return t('comments.noText');
  };
  const selectComment = useCommentOverlayStore((state) => state.selectComment);
  const activeCommentId = useCommentOverlayStore((state) => state.activeCommentId);

  const {
    comments,
    isLoading,
    resolveComment,
    reopenComment,
    isResolving,
    isReopening,
  } = useDiagramComments(diagramId, currentUserId);

  const { error, isFetching, isError } = useDiagramCommentsQuery(diagramId ?? '', undefined, {
    enabled: Boolean(diagramId),
  });

  const filteredThreads = useMemo(() => {
    const normalised = normaliseSearchTerm(searchTerm);
    if (!normalised) {
      return comments;
    }
    const matchesNode = (node: CommentNode): boolean => {
      const body = node.body?.toLowerCase() ?? '';
      if (body.includes(normalised)) {
        return true;
      }
      const displayName = buildDisplayName(node.authorId, node.author ?? null).toLowerCase();
      if (displayName.includes(normalised)) {
        return true;
      }
      return (node.replies ?? []).some(matchesNode);
    };

    return comments.filter(matchesNode);
  }, [comments, searchTerm]);

  const isInitialLoading = isLoading && comments.length === 0;

  const showEmptyState = !isInitialLoading && filteredThreads.length === 0 && searchTerm.trim().length === 0;
  const showSearchEmptyState = !isInitialLoading && filteredThreads.length === 0 && searchTerm.trim().length > 0;
  const showContent = !isInitialLoading && !isError;

  const orgProjection = useMemo(() => {
    if (diagramType !== 'org') {
      return { nodes, edges };
    }
    return OrgChartPerspectiveProjection.compute(nodes, edges, { asOfDate, activeViewpoint });
  }, [diagramType, nodes, edges, asOfDate, activeViewpoint]);

  const collapseProjection = useMemo(() => {
    if (diagramType !== 'org') {
      return { nodes: orgProjection.nodes, edges: orgProjection.edges };
    }
    return applyOrgChartCollapse(orgProjection.nodes, orgProjection.edges, collapsedNodeIds);
  }, [diagramType, orgProjection.nodes, orgProjection.edges, collapsedNodeIds]);

  const hiddenState = useMemo(() => {
    if (diagramType !== 'org') {
      return {
        projectedNodeIds: new Set<string>(),
        renderedNodeIds: new Set<string>(),
        findCollapsedAncestor: (_shapeId: string) => null as string | null,
      };
    }

    const projectedNodeIds = new Set(orgProjection.nodes.map((node) => node.id));
    const renderedNodeIds = new Set(collapseProjection.nodes.map((node) => node.id));
    const reportingTargets = computeOrgReportingTargets(orgProjection.nodes, orgProjection.edges);
    const resolveReportingNodeId = (nodeId: string): string => reportingTargets.get(nodeId) ?? nodeId;
    const parentsByChild = new Map<string, Set<string>>();

    orgProjection.edges.forEach((edge) => {
      if (!isReportsToEdge(edge) && !isMatrixEdge(edge)) return;
      const fromId = resolveReportingNodeId(edge.from);
      const toId = resolveReportingNodeId(edge.to);
      if (fromId === toId) return;
      if (!projectedNodeIds.has(fromId) || !projectedNodeIds.has(toId)) return;
      const parents = parentsByChild.get(fromId) ?? new Set<string>();
      parents.add(toId);
      parentsByChild.set(fromId, parents);
    });

    const findCollapsedAncestor = (shapeId: string): string | null => {
      const startId = resolveReportingNodeId(shapeId);
      const queue: string[] = [startId];
      const visited = new Set<string>();

      while (queue.length > 0) {
        const current = queue.shift();
        if (!current || visited.has(current)) continue;
        visited.add(current);
        const parents = parentsByChild.get(current);
        if (!parents) continue;
        for (const parent of parents) {
          if (collapsedNodeIds.includes(parent)) {
            return parent;
          }
          if (!visited.has(parent)) queue.push(parent);
        }
      }

      return null;
    };

    return { projectedNodeIds, renderedNodeIds, findCollapsedAncestor };
  }, [diagramType, orgProjection.nodes, orgProjection.edges, collapseProjection.nodes, collapsedNodeIds]);

  const handleSelectComment = (comment: CommentNode, expandTargetId: string | null) => {
    if (expandTargetId) {
      toggleCollapsed(expandTargetId);
    }
    selectComment(comment.id);
  };

  const handleResolveToggle = async (comment: CommentNode) => {
    try {
      if (comment.state === 'resolved') {
        await reopenComment(comment.id);
      } else {
        await resolveComment(comment.id);
      }
    } catch (err) {
      logger.error('Failed to toggle comment state', err);
    }
  };

  const renderWithMentions = useMemo(() => {
    return (body: string) => {
      if (!body) return null;
      const segments: React.ReactNode[] = [];
      const regex = /@user\{([^}|]+)(?:\|([^}]+))?\}/g;
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = regex.exec(body)) !== null) {
        const [full, id, label] = match;
        if (match.index > lastIndex) {
          segments.push(body.slice(lastIndex, match.index));
        }
        const resolved = mentionableMap.get(id);
        const display = resolved?.name?.trim() || resolved?.email?.trim() || label || id;
        segments.push(
          <span
            key={`${id}-${match.index}`}
            className="inline-flex items-center rounded font-medium text-primary hover:bg-primary/10"
          >
            @{display}
          </span>,
        );
        lastIndex = match.index + full.length;
      }

      if (lastIndex < body.length) {
        segments.push(body.slice(lastIndex));
      }

      return segments;
    };
  }, [mentionableMap]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="border-b px-3 py-2">
        <div className="space-y-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('comments.search')}
              className="pl-9"
              aria-label={t('comments.searchAriaLabel')}
            />
          </div>
          {isAuthenticated ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleBeginCommentPlacement}
              data-testid="tid.comments.new-comment"
              className="w-full"
            >
              <MessageCirclePlus className="mr-1.5 h-4 w-4" />
              {t('comments.addComment')}
            </Button>
          ) : null}
        </div>
      </div>

      {isInitialLoading ? (
        <div className="flex flex-1 items-center justify-center px-4 text-sm text-muted-foreground">
          {t('comments.loading')}
        </div>
      ) : null}

      {isError && error ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center">
          <span className="text-sm font-medium text-destructive">{t('comments.unableToLoad')}</span>
          <span className="text-xs text-muted-foreground">{error.message}</span>
        </div>
      ) : null}

      {showContent ? (
        <>
          {isFetching ? (
            <LinearProgress />
          ) : null}

          {showEmptyState ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-sm text-muted-foreground">
              {t('comments.noComments')}
            </div>
          ) : null}

          {showSearchEmptyState ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-sm text-muted-foreground">
              {t('comments.noMatches')}
            </div>
          ) : null}

          {(filteredThreads.length > 0 || searchTerm.trim().length > 0) && !showSearchEmptyState ? (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <TooltipProvider>
                <ul className="space-y-0">
                  {filteredThreads.map((comment) => {
                    const replyCount = countReplies(comment.replies);
                    const createdRelative = formatRelativeTime(comment.createdAt, timeLocale);
                    const preview = buildThreadPreview(comment);
                    const isActive = activeCommentId === comment.id;
                    const shapeId = comment.shapeId;
                    const isHiddenByFilters = Boolean(shapeId && diagramType === 'org' && !hiddenState.projectedNodeIds.has(shapeId));
                    const isHiddenByCollapse = Boolean(
                      shapeId &&
                      diagramType === 'org' &&
                      hiddenState.projectedNodeIds.has(shapeId) &&
                      !hiddenState.renderedNodeIds.has(shapeId),
                    );
                    const expandTargetId = isHiddenByCollapse && shapeId
                      ? hiddenState.findCollapsedAncestor(shapeId)
                      : null;
                    const resolvingState =
                    comment.state === 'resolved' ? isReopening : isResolving;
                  const author = resolveCommentAuthorVisual(
                    comment.authorId ?? null,
                    comment.author ?? null,
                    comment.metadata?.color ?? null,
                  );

                  return (
                    <li
                      key={comment.id}
                      className={cn(
                        'border-b last:border-b-0 transition-colors',
                        isActive ? 'bg-muted' : 'hover:bg-muted/50',
                      )}
                    >
                      <div className="px-3 py-3">
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            onClick={() => handleSelectComment(comment, expandTargetId)}
                            className="flex flex-1 flex-col gap-1 text-left"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-7 w-7">
                                  <AvatarFallback
                                    className="bg-transparent font-semibold text-white text-xs"
                                    style={{ backgroundColor: author.color }}
                                  >
                                    {author.fallbackText}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm font-medium text-foreground">{author.displayName}</span>
                              </div>
                            </div>
                            <span className="text-xs text-muted-foreground">{createdRelative}</span>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {renderWithMentions(preview)}
                            </p>
                            {isHiddenByFilters ? (
                              <span className="text-xs text-muted-foreground">
                                {t('comments.hiddenByFilters', 'Hidden by filters')}
                              </span>
                            ) : null}
                            {replyCount > 0 && (
                              <span className="text-xs text-primary">
                                {replyCount === 1 ? `1 ${t('comments.reply')}` : `${replyCount} ${t('comments.replies')}`}
                              </span>
                            )}
                          </button>
                          <div className="flex items-start">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-full hover:bg-muted flex-shrink-0"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleResolveToggle(comment);
                                  }}
                                  disabled={resolvingState}
                                  aria-label={comment.state === 'resolved' ? t('comments.reopenComment') : t('comments.resolveComment')}
                                >
                                  {comment.state === 'resolved' ? (
                                    <RotateCcw className="h-4 w-4" />
                                  ) : (
                                    <CheckCircle2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {comment.state === 'resolved' ? t('comments.reopenComment') : t('comments.resolveComment')}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
                </ul>
              </TooltipProvider>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
